import { Button, Input, Message, Select, Switch, Typography } from "@arco-design/web-react";
import { Copy, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { AdminEditorFormRow } from "../../../shared/ui/AdminEditorFormRow";
import { copyTextToClipboard } from "../../../lib/clipboard";
import { formatContactStarOptionLabel } from "../config";
import type {
  ContactWorkbenchDetail,
  ContactWorkbenchOrganizationOption,
  ContactWorkbenchOrganizationUnitOption,
  ContactWorkbenchUpdatePayload,
} from "../types";

export type ContactEditorDraft = {
  orgId?: number;
  orgName: string;
  mainOrgUnitId?: number;
  contactName: string;
  mobile: string;
  backupMobile: string;
  certNumber: string;
  wechat: string;
  positionName: string;
  contactStar: string;
  hobbyNote: string;
  tastePreferenceNote: string;
  giftInterestNote: string;
  isKeyContact: boolean;
  isActive: boolean;
  remark: string;
};

/** 联系人资料编辑。 */
export function ContactEditor({
  draft,
  detail,
  contactStarOptions,
  mutating,
  canEdit,
  actionPlacement = "title",
  onSearchOrganizations,
  onSearchOrganizationUnits,
  onDraftChange,
  onSave,
}: {
  draft: ContactEditorDraft | null;
  detail?: ContactWorkbenchDetail;
  contactStarOptions: string[];
  mutating: boolean;
  canEdit: boolean;
  /** title：标题旁保存（桌面）；footer：底部固定保存（窄屏抽屉）。 */
  actionPlacement?: "title" | "footer";
  onSearchOrganizations: (keyword: string) => Promise<ContactWorkbenchOrganizationOption[]>;
  onSearchOrganizationUnits: (
    orgId: number,
    keyword: string,
  ) => Promise<ContactWorkbenchOrganizationUnitOption[]>;
  onDraftChange: (draft: ContactEditorDraft) => void;
  onSave: () => void;
}) {
  const [organizationOptions, setOrganizationOptions] = useState<ContactWorkbenchOrganizationOption[]>(
    [],
  );
  const [unitOptions, setUnitOptions] = useState<ContactWorkbenchOrganizationUnitOption[]>([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const unitSearchSequenceRef = useRef(0);

  useEffect(() => {
    if (!draft) {
      setOrganizationOptions([]);
      setUnitOptions([]);
      return;
    }
    if (detail) {
      setOrganizationOptions([{ id: detail.orgId, orgName: detail.orgName }]);
      setUnitOptions(
        detail.mainOrgUnitId
          ? [
              {
                id: detail.mainOrgUnitId,
                orgId: detail.orgId,
                unitName: detail.mainOrgUnitName ?? detail.mainOrgUnitPathName ?? '',
                unitPathName: detail.mainOrgUnitPathName ?? detail.mainOrgUnitName ?? '',
                levelNo: 0,
              },
            ]
          : [],
      );
      return;
    }
    setOrganizationOptions(
      draft.orgId && draft.orgName ? [{ id: draft.orgId, orgName: draft.orgName }] : [],
    );
  }, [detail, draft?.orgId, draft?.orgName, draft?.mainOrgUnitId]);

  if (!draft) {
    return null;
  }
  const mobileValidationError = getMobileValidationError(draft.mobile);
  const selectedUnitOption = filterUnitOptionsByOrgId(unitOptions, draft.orgId).find(
    (option) => option.id === draft.mainOrgUnitId,
  );
  const unitCopyText =
    selectedUnitOption?.unitPathName ||
    selectedUnitOption?.unitName ||
    detail?.mainOrgUnitPathName ||
    detail?.mainOrgUnitName ||
    "";

  /** 远程搜索学校候选项，并保留当前已选学校。 */
  async function handleSearchOrganizations(keyword: string) {
    setOrganizationLoading(true);
    try {
      const options = await onSearchOrganizations(keyword);
      setOrganizationOptions(mergeOrganizationOptions(options, draft));
    } finally {
      setOrganizationLoading(false);
    }
  }

  /** 远程搜索当前学校下的院系专业候选项。 */
  async function handleSearchUnits(keyword: string) {
    const orgId = draft.orgId;
    if (!orgId) {
      setUnitOptions([]);
      return;
    }
    const searchSequence = unitSearchSequenceRef.current + 1;
    unitSearchSequenceRef.current = searchSequence;
    setUnitLoading(true);
    try {
      const options = await onSearchOrganizationUnits(orgId, keyword);
      if (unitSearchSequenceRef.current === searchSequence) {
        setUnitOptions(mergeUnitOptions(options, draft, detail));
      }
    } finally {
      if (unitSearchSequenceRef.current === searchSequence) {
        setUnitLoading(false);
      }
    }
  }

  /** 选择学校后写入学校 ID/名称，并清空旧院系专业。 */
  function handleOrganizationSelect(value: unknown) {
    const orgId = value == null || value === "" ? undefined : Number(value);
    const selected = organizationOptions.find((option) => option.id === orgId);
    const searchSequence = unitSearchSequenceRef.current + 1;
    unitSearchSequenceRef.current = searchSequence;
    onDraftChange({
      ...draft,
      orgId,
      orgName: selected?.orgName ?? "",
      mainOrgUnitId: undefined,
    });
    setUnitOptions([]);
    if (!orgId) {
      return;
    }
    void onSearchOrganizationUnits(orgId, "").then((options) => {
      if (unitSearchSequenceRef.current === searchSequence) {
        setUnitOptions(filterUnitOptionsByOrgId(options, orgId));
      }
    });
  }

  return (
    <section
      className={[
        "crm-contact-workbench-detail-section",
        "admin-editor-form",
        actionPlacement === "footer" ? "is-action-footer" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="crm-contact-workbench-detail-title">
        <Typography.Title heading={6}>联系人资料</Typography.Title>
        {actionPlacement === "title" ? (
          <Button
            type="primary"
            size="small"
            icon={<Save size={14} />}
            loading={mutating}
            disabled={
              !canEdit ||
              !draft.orgId ||
              !draft.contactName.trim() ||
              Boolean(mobileValidationError)
            }
            onClick={onSave}
          >
            保存
          </Button>
        ) : null}
      </div>
      <div className="crm-contact-workbench-detail-scroll">
      <AdminEditorFormRow label="学校/单位" required>
        <div className="crm-contact-workbench-copyable-control">
          <Select
            allowClear
            showSearch
            disabled={!canEdit}
            data-testid="contact-org-select"
            loading={organizationLoading}
            value={draft.orgId}
            placeholder="搜索学校名称"
            filterOption={false}
            dropdownMenuClassName="crm-contact-workbench-long-option-dropdown"
            triggerProps={{
              autoFitPosition: true,
              boundaryDistance: { right: 8, bottom: 8 },
              position: "bl",
            }}
            onSearch={(keyword) => void handleSearchOrganizations(keyword)}
            onFocus={() => void handleSearchOrganizations(draft.orgName || "")}
            onChange={handleOrganizationSelect}
          >
            {organizationOptions.map((option) => {
              const title = formatOrganizationOption(option);
              return (
                <Select.Option key={option.id} value={option.id}>
                  <LongSelectOptionLabel title={title} />
                </Select.Option>
              );
            })}
          </Select>
          <FieldCopyButton
            label="学校/单位"
            value={
              draft.orgName ||
              organizationOptions.find((option) => option.id === draft.orgId)?.orgName ||
              detail?.orgName ||
              ""
            }
          />
        </div>
      </AdminEditorFormRow>
      <AdminEditorFormRow label="院系专业">
        <div className="crm-contact-workbench-copyable-control">
          <Select
            allowClear
            showSearch
            disabled={!canEdit || !draft.orgId}
            data-testid="contact-unit-select"
            loading={unitLoading}
            value={draft.mainOrgUnitId}
            placeholder={draft.orgId ? "搜索院系/专业" : "请先选择学校"}
            filterOption={false}
            dropdownMenuClassName="crm-contact-workbench-long-option-dropdown"
            triggerProps={{
              autoFitPosition: true,
              boundaryDistance: { right: 8, bottom: 8 },
              position: "bl",
            }}
            onSearch={(keyword) => void handleSearchUnits(keyword)}
            onFocus={() => void handleSearchUnits("")}
            onChange={(mainOrgUnitId) =>
              onDraftChange({
                ...draft,
                mainOrgUnitId: mainOrgUnitId ? Number(mainOrgUnitId) : undefined,
              })
            }
          >
            {filterUnitOptionsByOrgId(unitOptions, draft.orgId).map((option) => {
              const title = option.unitPathName || option.unitName;
              return (
                <Select.Option key={option.id} value={option.id}>
                  <LongSelectOptionLabel title={title} />
                </Select.Option>
              );
            })}
          </Select>
          <FieldCopyButton label="院系专业" value={unitCopyText} />
        </div>
      </AdminEditorFormRow>
      <AdminEditorFormRow label="联系人姓名" required>
        <Input
          disabled={!canEdit}
          value={draft.contactName}
          onChange={(contactName) => onDraftChange({ ...draft, contactName })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="职务">
        <Input
          disabled={!canEdit}
          value={draft.positionName}
          onChange={(positionName) => onDraftChange({ ...draft, positionName })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="手机" error={mobileValidationError}>
        <Input
          disabled={!canEdit}
          inputMode="numeric"
          value={draft.mobile}
          onChange={(mobile) => onDraftChange({ ...draft, mobile })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="其他电话">
        <Input
          disabled={!canEdit}
          maxLength={30}
          value={draft.backupMobile}
          onChange={(backupMobile) => onDraftChange({ ...draft, backupMobile })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="身份证号">
        <Input
          disabled={!canEdit}
          maxLength={100}
          value={draft.certNumber}
          onChange={(certNumber) => onDraftChange({ ...draft, certNumber })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="已加客户微信">
        <Input
          disabled={!canEdit}
          value={draft.wechat}
          onChange={(wechat) => onDraftChange({ ...draft, wechat })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="星级">
        <div className="crm-contact-workbench-copyable-control">
          <Select
            allowClear
            disabled={!canEdit}
            data-testid="contact-star-select"
            placeholder="请选择星级"
            value={draft.contactStar || undefined}
            onChange={(contactStar) =>
              onDraftChange({ ...draft, contactStar: contactStar ? String(contactStar) : "" })
            }
          >
            {resolveContactStarSelectOptions(contactStarOptions, draft.contactStar).map((option) => (
              <Select.Option key={option} value={option} title={formatContactStarOptionLabel(option)}>
                {option}
              </Select.Option>
            ))}
          </Select>
          <FieldCopyButton label="星级" value={draft.contactStar} />
        </div>
      </AdminEditorFormRow>
      <AdminEditorFormRow label="关键联系人" className="admin-editor-form-row--switch">
        <Switch
          disabled={!canEdit}
          checked={draft.isKeyContact}
          onChange={(isKeyContact) => onDraftChange({ ...draft, isKeyContact })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="在岗" className="admin-editor-form-row--switch">
        <Switch
          disabled={!canEdit}
          checked={draft.isActive}
          onChange={(isActive) => onDraftChange({ ...draft, isActive })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="爱好">
        <Input.TextArea
          disabled={!canEdit}
          autoSize={{ minRows: 2, maxRows: 4 }}
          maxLength={200}
          value={draft.hobbyNote}
          onChange={(hobbyNote) => onDraftChange({ ...draft, hobbyNote })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="烟酒茶咖吃">
        <Input.TextArea
          disabled={!canEdit}
          autoSize={{ minRows: 2, maxRows: 4 }}
          maxLength={500}
          value={draft.tastePreferenceNote}
          onChange={(tastePreferenceNote) =>
            onDraftChange({ ...draft, tastePreferenceNote })
          }
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="感兴趣的东西（送礼用）">
        <Input.TextArea
          disabled={!canEdit}
          autoSize={{ minRows: 2, maxRows: 4 }}
          maxLength={500}
          value={draft.giftInterestNote}
          onChange={(giftInterestNote) => onDraftChange({ ...draft, giftInterestNote })}
        />
      </AdminEditorFormRow>
      <AdminEditorFormRow label="备注">
        <Input.TextArea
          disabled={!canEdit}
          autoSize={{ minRows: 3, maxRows: 5 }}
          value={draft.remark}
          onChange={(remark) => onDraftChange({ ...draft, remark })}
        />
      </AdminEditorFormRow>
      </div>
      {actionPlacement === "footer" ? (
        <div className="crm-contact-workbench-detail-action-bar">
          <Button
            type="primary"
            long
            icon={<Save size={14} />}
            loading={mutating}
            disabled={
              !canEdit ||
              !draft.orgId ||
              !draft.contactName.trim() ||
              Boolean(mobileValidationError)
            }
            onClick={onSave}
          >
            保存
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** 合并学校搜索结果和当前已选学校。 */
function mergeOrganizationOptions(
  options: ContactWorkbenchOrganizationOption[],
  draft: ContactEditorDraft,
) {
  const current =
    draft.orgId && draft.orgName ? [{ id: draft.orgId, orgName: draft.orgName }] : [];
  return dedupeById([...current, ...options]);
}

/** 合并院系搜索结果和当前已选院系。 */
function mergeUnitOptions(
  options: ContactWorkbenchOrganizationUnitOption[],
  draft: ContactEditorDraft,
  detail?: ContactWorkbenchDetail,
) {
  const orgScopedOptions = filterUnitOptionsByOrgId(options, draft.orgId);
  const current =
    draft.mainOrgUnitId && draft.orgId
      ? [
          {
            id: draft.mainOrgUnitId,
            orgId: draft.orgId,
            unitName: detail?.mainOrgUnitName ?? detail?.mainOrgUnitPathName ?? "",
            unitPathName: detail?.mainOrgUnitPathName ?? detail?.mainOrgUnitName ?? "",
            levelNo: 0,
          },
        ]
      : [];
  return dedupeById([...current, ...orgScopedOptions]);
}

/** 把当前下拉展示值复制到剪贴板。 */
function copyFieldValue(label: string, value: string) {
  const text = value.trim();
  if (!text) {
    Message.warning(`暂无${label}可复制`);
    return;
  }
  // 必须在点击同步栈内启动复制；成功提示在 Promise 后再发。
  void copyTextToClipboard(text)
    .then(() => {
      Message.success(`已复制${label}：${text}`);
    })
    .catch(() => {
      Message.error("复制失败，请手动选择文本后 Ctrl+C");
    });
}

/** 下拉旁复制当前选中展示值。 */
function FieldCopyButton({ label, value }: { label: string; value: string }) {
  const canCopy = Boolean(value.trim());
  return (
    <Button
      type="text"
      size="mini"
      htmlType="button"
      icon={<Copy size={14} />}
      aria-label={`复制${label}`}
      title={canCopy ? `复制${label}` : `暂无${label}可复制`}
      disabled={!canCopy}
      onMouseDown={(event) => {
        // 阻止按钮抢焦点打乱 Select，但不要 preventDefault，以免丢掉用户手势。
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        copyFieldValue(label, value);
      }}
    />
  );
}

/** 格式化学校候选项显示。 */
function formatOrganizationOption(option: ContactWorkbenchOrganizationOption) {
  const area = [option.province, option.city].filter(Boolean).join(" / ");
  return area ? `${option.orgName} (${area})` : option.orgName;
}

/** 在候选弹层内保留完整文本，并提供悬停标题作为辅助查看方式。 */
function LongSelectOptionLabel({ title }: { title: string }) {
  return (
    <span className="crm-contact-workbench-long-option-label" title={title}>
      {title}
    </span>
  );
}

/** 从联系人详情生成编辑草稿。 */
export function createContactDraft(detail: ContactWorkbenchDetail): ContactEditorDraft {
  return {
    orgId: detail.orgId,
    orgName: detail.orgName,
    mainOrgUnitId: detail.mainOrgUnitId,
    contactName: detail.contactName,
    mobile: detail.mobile ?? "",
    backupMobile: detail.backupMobile ?? "",
    certNumber: detail.certNumber ?? "",
    wechat: detail.wechat ?? "",
    positionName: detail.positionName ?? "",
    contactStar: detail.contactStar ?? "",
    hobbyNote: detail.hobbyNote ?? "",
    tastePreferenceNote: detail.tastePreferenceNote ?? "",
    giftInterestNote: detail.giftInterestNote ?? "",
    isKeyContact: detail.isKeyContact,
    isActive: detail.isActive,
    remark: detail.remark ?? "",
  };
}

/** 新建联系人空草稿。 */
export function createEmptyContactDraft(): ContactEditorDraft {
  return {
    orgName: "",
    contactName: "",
    mobile: "",
    backupMobile: "",
    certNumber: "",
    wechat: "",
    positionName: "",
    contactStar: "",
    hobbyNote: "",
    tastePreferenceNote: "",
    giftInterestNote: "",
    isKeyContact: false,
    isActive: true,
    remark: "",
  };
}

/** 把编辑草稿映射为保存请求。 */
export function mapContactDraftToPayload(draft: ContactEditorDraft): ContactWorkbenchUpdatePayload {
  return {
    contactName: draft.contactName,
    orgId: draft.orgId as number,
    mainOrgUnitId: draft.mainOrgUnitId,
    mobile: draft.mobile,
    backupMobile: draft.backupMobile,
    certNumber: draft.certNumber,
    wechat: draft.wechat,
    positionName: draft.positionName,
    contactStar: draft.contactStar,
    hobbyNote: draft.hobbyNote,
    tastePreferenceNote: draft.tastePreferenceNote,
    giftInterestNote: draft.giftInterestNote,
    isKeyContact: draft.isKeyContact,
    isActive: draft.isActive,
    remark: draft.remark,
  };
}

/** 手机可留空；填写时必须是 11 位 ASCII 数字。 */
export function getMobileValidationError(mobile: string): string {
  const normalized = mobile.trim();
  return normalized && !/^\d{11}$/.test(normalized) ? "手机必须为11位数字" : "";
}

/** 只保留当前学校下的院系专业候选项。 */
function filterUnitOptionsByOrgId(
  options: ContactWorkbenchOrganizationUnitOption[],
  orgId?: number,
) {
  if (!orgId) {
    return [];
  }
  return options.filter((option) => option.orgId === orgId);
}

/** 去除重复 ID。 */
function dedupeById<T extends { id: number }>(values: T[]) {
  const ids = new Set<number>();
  return values.filter((item) => {
    if (ids.has(item.id)) {
      return false;
    }
    ids.add(item.id);
    return true;
  });
}

/** 编辑下拉保留当前历史星级值，避免旧数据无法回显。 */
function resolveContactStarSelectOptions(options: string[], currentValue?: string) {
  const normalized = (currentValue ?? "").trim();
  if (!normalized || options.includes(normalized)) {
    return options;
  }
  return [...options, normalized];
}
